import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { InfoboxService } from '../infobox/infobox.service';
import { inject } from '@angular/core';
import { catchError, from, mergeMap, throwError } from 'rxjs';
import {
	MESSAGE_CHECK_IN_ERROR,
	MESSAGE_CHECK_OUT_ERROR,
} from '@/new-transport/transport-timetable/_store/transport-timetable.store';
import { BANK_ACCOUNT_ERROR_MESSAGE } from '@/organization/organization/organization-bank-accounts/organization-bank-accounts.component';
import { USER_ALREADY_EXISTS_ERROR } from '@/reservation/reservation-summary/reservation-summary-owner/reservation-summary-owner-dialog/reservation-summary-owner-dialog.component';
import { StatusCode } from '../enums/StatusCode';
import { COMFORT_PACKAGE_POOL_CONFLICTS_ERROR } from '@/bag/bag-transport-term/bag-transport-term.model';

const ERRORS_TO_IGNORE = [
	USER_ALREADY_EXISTS_ERROR,
	StatusCode.CONFLICT_RESPONSE_ERROR,
	BANK_ACCOUNT_ERROR_MESSAGE,
	MESSAGE_CHECK_IN_ERROR,
	MESSAGE_CHECK_OUT_ERROR,
	COMFORT_PACKAGE_POOL_CONFLICTS_ERROR,
];

const STATUS_MESSAGES: Record<number, string> = {
	[StatusCode.UNAUTHORIZED]: 'Sesja wygasła. Zaloguj się ponownie.',
	[StatusCode.FORBIDDEN]: 'Brak uprawnień do wykonania tej operacji.',
	[StatusCode.NOT_FOUND]: 'Nie znaleziono zasobu lub już nie istnieje.',
	[StatusCode.INTERNAL_SERVER_ERROR]: 'Wystąpił problem z serwerem. Spróbuj ponownie.',
	[StatusCode.BAD_GATEWAY]: 'Błąd pośredniego serwera (502). Spróbuj ponownie.',
	[StatusCode.SERVICE_UNAVAILABLE]: 'Usługa chwilowo niedostępna (503). Spróbuj ponownie później.',
	[StatusCode.GATEWAY_TIMEOUT]: 'Przekroczono czas odpowiedzi (504). Spróbuj ponownie.',
	[StatusCode.VALIDATION_ERROR]: 'Błąd walidacji (422). Popraw dane.',
	0: 'Brak połączenia z siecią lub problem CORS. Sprawdź internet/VPN i spróbuj ponownie.',
};

const showHttpErrorMessage = (err: HttpErrorResponse, infobox: InfoboxService): void => {
	if (err.status === StatusCode.BAD_REQUEST) {
		const nonFieldErrors = (err.error as { nonFieldErrors?: string[]; non_field_errors?: string[] })
			?.nonFieldErrors;
		const nonFieldErrorsSnake = (err.error as { nonFieldErrors?: string[]; non_field_errors?: string[] })
			?.non_field_errors;
		const mergedNonFieldErrors = nonFieldErrors ?? nonFieldErrorsSnake;
		if (Array.isArray(mergedNonFieldErrors) && mergedNonFieldErrors.length > 0) {
			infobox.error(mergedNonFieldErrors.join('<br>'));
		} else {
			infobox.error(JSON.stringify(err.error));
		}
		return;
	}

	const msg = STATUS_MESSAGES[err.status] ?? 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.';
	if (err.status === StatusCode.VALIDATION_ERROR && err.error?.message) {
		let errorMessage = err.error.message;
		try {
			const parsedMessage = JSON.parse(errorMessage);
			if (typeof parsedMessage === 'object' && parsedMessage !== null) {
				errorMessage = Object.entries(parsedMessage)
					.map(([field, error]) => `${field}: ${error}`)
					.join('<br>');
			}
		} catch {}

		infobox.error(msg + '<br>' + errorMessage);
	} else {
		infobox.error(msg);
	}
};

const handleBlobHttpError = (err: HttpErrorResponse, infobox: InfoboxService) => {
	const blobError = err.error as Blob & { text?: () => Promise<string> };
	if (typeof blobError.text !== 'function') {
		showHttpErrorMessage(err, infobox);
		console.error('HTTP error:', err);
		return throwError(() => err);
	}

	return from(blobError.text()).pipe(
		mergeMap((blobText: string) => {
			let parsedBlobError: unknown = blobText;
			try {
				parsedBlobError = JSON.parse(blobText);
			} catch {}
			const blobErr = new HttpErrorResponse({
				error: parsedBlobError,
				headers: err.headers,
				status: err.status,
				statusText: err.statusText,
				url: err.url ?? undefined,
			});
			showHttpErrorMessage(blobErr, infobox);
			console.error('HTTP error:', blobErr);
			return throwError(() => blobErr);
		}),
	);
};

export const httpErrorsInterceptor: HttpInterceptorFn = (req, next) => {
	const infobox = inject(InfoboxService);

	return next(req).pipe(
		catchError((err: unknown) => {
			if (ERRORS_TO_IGNORE.some((errToIgnore) => JSON.stringify(err).includes(errToIgnore.toString()))) {
				return throwError(() => err);
			}
			if (err instanceof HttpErrorResponse) {
				if (err.error instanceof Blob) {
					return handleBlobHttpError(err, infobox);
				}
				showHttpErrorMessage(err, infobox);
				console.error('HTTP error:', err);
			}

			return throwError(() => err);
		}),
	);
};
